import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  confirmSuggestion,
  dismissSuggestion,
  findApplications,
  suggestionsQuery,
  type Suggestion,
} from '../../api/businessAppsDetect'
import { moduleOf, shellQuery } from '../../api/shell'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { appUrl, failed } from '../parts'
import { CatalogModal } from './CatalogModal'

/**
 * Overview > Applications > Suggested: ce aplicatii par sa fie pe masinile
 * clientului, dupa software-ul instalat, servicii, instantele de baza de date
 * si porturi.
 *
 * Omul bifeaza componentele si confirma: aplicatia se face (sau se completeaza)
 * pe aceleasi cai ca "Add application" si "Add component". Statiile cu clientul
 * se numara; se adauga numai daca omul bifeaza "Add the workstations" (implicit
 * nu). "Dismiss" ascunde sugestia pana apare ceva nou.
 */
export const SUGGESTION_STYLES = ['css/business_apps_detect.css']

export function SuggestionsPanel() {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(SUGGESTION_STYLES, shell.data?.version ?? '')
  const page = useQuery(suggestionsQuery())
  const query = useQueryClient()
  const [catalog, setCatalog] = useState(false)

  const run = useMutation({
    mutationFn: (companyId: number) => findApplications(companyId),
    onSuccess: (result) => {
      toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
      void query.invalidateQueries({ queryKey: ['business-apps', 'suggestions'] })
    },
    onError: (error) => toast(failed(error), 'Applications', 'error'),
  })

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  if (!data.ready) {
    return (
      <div className="alert alert-warning" style={{ margin: 20 }}>
        <i className="fas fa-database"></i> {data.message ?? 'Application discovery is not set up on this server yet.'}
      </div>
    )
  }
  /* Numai firma din bara (si cu "All companies"); fara ea nu se cauta nimic. */
  if (data.needsCompany === true || data.companyId < 1) {
    return (
      <div className="alert alert-info" id="badNeedsCompany" style={{ margin: 20 }}>
        <i className="fas fa-building"></i> Choose a customer in the company selector at the top to look for its applications.
      </div>
    )
  }
  const target = data.companyId

  return (
    <>
      <div className="bad-toolbar">
        {data.canEdit && (
          <>
            <button type="button" className="btn btn-primary btn-sm" id="badRun" disabled={run.isPending} onClick={() => run.mutate(target)}>
              <i className={run.isPending ? 'fas fa-spinner fa-spin' : 'fas fa-search'}></i> Find applications
            </button>
          </>
        )}
        <span className="bad-last">{data.lastRun ? `Last looked: ${data.lastRun}` : 'Not looked yet.'}</span>
        <span className="bad-spacer"></span>
        <button type="button" className="btn btn-default btn-sm" id="badCatalog" onClick={() => setCatalog(true)}>
          <i className="fas fa-book"></i> Catalog
        </button>
      </div>

      {data.suggestions.length === 0 ? (
        <div className="adm-card bad-empty">
          <p className="text-muted" style={{ padding: '10px 15px', margin: 0 }}>
            No suggestion right now. "Find applications" looks at the installed software, the Windows services, the
            database instances and the listening ports of this customer's machines,
            and proposes the business applications it recognises.
          </p>
        </div>
      ) : (
        data.suggestions.map((s) => <SuggestionCard key={s.id} suggestion={s} canEdit={data.canEdit} />)
      )}

      {catalog && <CatalogModal onClose={() => setCatalog(false)} />}
    </>
  )
}

const CONFIDENCE_PILL: Record<number, string> = { 3: 'adm-pill-ok', 2: 'adm-pill-warn', 1: '' }

function SuggestionCard({ suggestion: s, canEdit }: { suggestion: Suggestion; canEdit: boolean }) {
  const query = useQueryClient()
  const [ticked, setTicked] = useState<Record<string, boolean>>(() => Object.fromEntries(s.components.map((c) => [c.key, true])))
  const [workstations, setWorkstations] = useState(false)
  const [name, setName] = useState(s.name)
  const [busy, setBusy] = useState(false)
  const chosen = s.components.filter((c) => ticked[c.key])
  const existing = s.existingAppId !== null

  const done = () => void query.invalidateQueries({ queryKey: ['business-apps'] })

  const confirm = () => {
    if (chosen.length === 0 && !workstations) {
      toast('Tick at least one component to add.', 'Applications', 'error')
      return
    }
    setBusy(true)
    confirmSuggestion(s.id, {
      components: chosen.map((c) => ({ device_id: c.deviceId, role: c.role })),
      add_workstations: workstations,
      name,
    })
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message, 'Applications', 'error')
          return
        }
        toast(result.message, 'Applications')
        done()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  const dismiss = () => {
    setBusy(true)
    dismissSuggestion(s.id)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        done()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="adm-card bad-card" data-suggestion={s.code}>
      <div className="adm-card-head">
        <h5>
          <i className="fas fa-cubes"></i> {s.name}
        </h5>
        {s.vendor !== '' && <span className="bad-vendor">{s.vendor}</span>}
        <span className={`adm-pill ${CONFIDENCE_PILL[s.confidence] ?? ''}`.trim()} title="How sure the match is">
          {s.confidenceLabel} confidence
        </span>
        {existing && s.existingAppId !== null && (
          <span className="bad-existing">
            Already recorded as <a href={appUrl(s.existingAppId)} target="_blank" rel="noopener noreferrer">{s.existingAppName}</a> - these are the new components
          </span>
        )}
      </div>
      <div className="bad-body">
        {s.description !== '' && <p className="bad-desc">{s.description}</p>}
        {s.components.length > 0 && (
          <table className="bad-components">
            <thead>
              <tr>
                <th></th>
                <th>Machine</th>
                <th>Role</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {s.components.map((c) => (
                <tr key={c.key}>
                  <td className="bad-check">
                    <input
                      type="checkbox"
                      aria-label={`Add ${c.deviceName} as ${c.roleLabel}`}
                      checked={ticked[c.key] ?? false}
                      disabled={!canEdit}
                      onChange={(e) => setTicked({ ...ticked, [c.key]: e.target.checked })}
                    />
                  </td>
                  <td>
                    <strong>{c.deviceName}</strong>
                    {!c.inCmdb && <span className="bad-nocmdb" title="Not in the CMDB yet: no impact link">not in CMDB</span>}
                  </td>
                  <td>{c.roleLabel}</td>
                  <td>
                    <ul className="bad-evidence">
                      {c.evidence.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {s.workstations.count > 0 && (
          <div className="bad-ws">
            <i className="fas fa-desktop"></i>{' '}
            {s.workstations.count === 1 ? '1 workstation has' : `${s.workstations.count} workstations have`} the client
            {s.workstations.names.length > 0 && (
              <span className="adm-muted">
                {' '}
                ({s.workstations.names.join(', ')}
                {s.workstations.count > s.workstations.names.length ? ', ...' : ''})
              </span>
            )}
            {canEdit && (
              <label>
                <input type="checkbox" checked={workstations} onChange={(e) => setWorkstations(e.target.checked)} /> Add the workstations as
                components too
              </label>
            )}
          </div>
        )}
        {canEdit && (
          <div className="bad-actions">
            {!existing && (
              <input className="form-control input-sm" aria-label="Application name" maxLength={190} value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <button type="button" className="btn btn-success btn-sm" disabled={busy} onClick={confirm}>
              <i className="fas fa-check"></i> {existing ? `Add to ${s.existingAppName ?? 'the application'}` : 'Create application'}
            </button>
            <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={dismiss}>
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
